/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, Input, Self, Optional } from '@angular/core';
import {
	UntypedFormGroup,
	UntypedFormBuilder,
	ControlValueAccessor,
	Validators,
	NgControl,
	ValidatorFn,
	AbstractControl,
	ValidationErrors,
	UntypedFormControl,
	FormGroupDirective,
	NgForm,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Observable, of, merge, combineLatest, BehaviorSubject, Subject } from 'rxjs';
import { filter, map, share, takeUntil } from 'rxjs/operators';
import { CustomFieldDefinition } from 'src/app/app-data/models/CustomFieldDefinition';
import { ICustomFieldDropDownItemDefinition } from 'src/app/app-data/models/CustomFieldDropDownDefinition';
import { filterDisabledDefinitions } from 'src/app/app-data/state/CustomFieldDefinitions';
import {
	TpFilterPredicateOp,
	DropDownPredicateOp,
	NumericPredicateOp,
	TextPredicateOp,
	ICustomFieldFilter,
	ITPFilterPredicateOpPair,
} from 'src/app/app-data/models/TPFilterPredicates';
import {
	NumericPredicateOpMap,
	TextPredicateOpMap,
	DropDownPredicateOpMap,
} from 'src/app/modules/reports/types/ReportCustomFieldFilterUtils';
import {
	DisambiguationEventTypes,
	UserSystemDisambiguation,
} from 'src/app/plain-javascript/services/UserSystemDisambiguation';

//data flow diagram (half-assed)
/*
- writeData (cva angular)
  - internalForm (operator/ value only)
	  - chipOperator$
		- (NYI) something that resets the form to default values on type change
	- externalCustomFieldIdCache
	  - internalForm.customField - no emit
		- chipOperator$
		- fieldLabel$
		- fieldType$
- customFieldDefinitions
  - customFieldDefinitionArray$
	- internalForm.customField - no emit
	- chipOperator$
	- fieldLabel$
	- fieldType$
- internalCustomFieldFilter.valueChanges (user input)
	- chipOperator$
	- fieldLabel$
	- fieldType$
	- external output
*/

/** Error when invalid control is dirty */
export class MyErrorStateMatcher implements ErrorStateMatcher {
	public isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
		return form && form.invalid && form.dirty;
	}
}

interface ICustomFieldInternalForm {
	customField?: CustomFieldDefinition;
	operator: ITPFilterPredicateOpPair;
	value?: string | number | string[];
}

const defaultOperator: TpFilterPredicateOp = TextPredicateOp.AnyValue;

function CustomFieldFilterCompleteValidator(internalGroup: UntypedFormGroup): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;
		if (!value) {
			return null;
		}
		return internalGroup.invalid ? { filterIncomplete: true } : null;
	};
}

@Component({
	selector: 'custom-field-filter',
	templateUrl: './custom-field-filter.component.html',
	styleUrls: ['./custom-field-filter.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{ provide: ErrorStateMatcher, useClass: MyErrorStateMatcher }],
})
export class CustomFieldFilterComponent implements OnInit, OnDestroy, ControlValueAccessor {
	constructor(@Self() @Optional() public control: NgControl, private formBuilder: UntypedFormBuilder) {
		if (this.control) {
			this.control.valueAccessor = this;
		}
	}

	//to reverse execution order
	//https://github.com/angular/angular/issues/29218
	private angularBugPatchStuff = {
		lastWriteValueArgs: null,
		initHasRan: () => this.internalCustomFieldFilter !== undefined,
	};

	@Input() public title: string;
	@Input() public customFieldDefinitions: Observable<Map<string, CustomFieldDefinition>>;

	public customFieldDefinitionsEnabledOnly$: Observable<Map<string, CustomFieldDefinition>>;

	public customFieldDefinitionArray$: Observable<CustomFieldDefinition[]>;

	public internalModel: UserSystemDisambiguation<ICustomFieldInternalForm>;

	private readonly numericPredicateOpPairs = Array.from(NumericPredicateOpMap.entries(), ([key, label]) => ({
		key,
		label,
	}));
	private readonly textPredicateOpPairs = Array.from(TextPredicateOpMap.entries(), ([key, label]) => ({
		key,
		label,
	}));
	private readonly dropDownPredicateOpPairs = Array.from(
		DropDownPredicateOpMap.entries(),
		([key, label]) => ({ key, label })
	);

	public internalCustomFieldFilter: UntypedFormGroup;

	public chipOperator$ = new BehaviorSubject<ITPFilterPredicateOpPair[]>(null);

	public fieldLabel$ = new BehaviorSubject<string>(null);
	public fieldType$ = new BehaviorSubject<string>(null);
	public customFieldDropDownDefinitions$ = new BehaviorSubject<ICustomFieldDropDownItemDefinition[]>(null);

	/** The most up to date custom field, fires on internal and external changes */
	public selectedCustomField$ = new BehaviorSubject(null);

	private shutdownSubject$ = new Subject<void>();

	private externalCustomFieldIdCache = new BehaviorSubject<string>(null);

	public ngOnInit(): void {
		window.bleh = this;
		this.internalCustomFieldFilter = this.formBuilder.group({
			customField: [undefined],
			operator: [undefined, Validators.required],
			value: [undefined, Validators.required],
		});

		this.internalModel = new UserSystemDisambiguation(this.internalCustomFieldFilter);

		this.control.control.setValidators([CustomFieldFilterCompleteValidator(this.internalCustomFieldFilter)]);
		this.control.control.updateValueAndValidity({ emitEvent: false });

		// See TP-3856 for details
		// more explicitly requires that the input observable exists at init time and fails more gracefully if it doesn't
		this.customFieldDefinitionsEnabledOnly$ =
			this.customFieldDefinitions.pipe(map((allDefsMap) => filterDisabledDefinitions(allDefsMap))) ||
			of(new Map<string, CustomFieldDefinition>());

		//register an ordered data structure for ui things (they shun maps)
		this.customFieldDefinitionArray$ = this.customFieldDefinitionsEnabledOnly$.pipe(
			map((defs) => (defs ? Array.from(defs.values()) : []))
		);

		//observable that represents "the currently selected customField"
		const externalCustomFieldChanged$ = combineLatest([
			this.customFieldDefinitionsEnabledOnly$,
			this.externalCustomFieldIdCache,
		]).pipe(
			// withLatestFrom(this.externalCustomFieldIdCache), //BLARG - expand into a full "programatic update fired" thing, use as pipeline
			map(([definitions, selected]) => {
				return definitions?.get(selected);
			}),
			filter((val) => this.internalCustomFieldFilter.get('customField').value !== val),
			share() // figure out why this was needed, it shouldn't be. Maybe need a defer on the of in the semiSafeCustomField?
		);

		externalCustomFieldChanged$.pipe(takeUntil(this.shutdownSubject$)).subscribe((updatedCustomField) => {
			//selected custom field needs to be updated
			this.internalModel.patchValue({ customField: updatedCustomField });
		});

		//either internal or external
		const customFieldChanged$ = merge(
			externalCustomFieldChanged$,
			this.internalCustomFieldFilter.get('customField').valueChanges
		); //BLARG - internalModel

		customFieldChanged$
			.pipe(
				map((field) => {
					switch (field?.view.type) {
						case 'number':
							return this.numericPredicateOpPairs;
						case 'dropDownList':
							return this.dropDownPredicateOpPairs;
						case 'text':
							return this.textPredicateOpPairs;
						case 'link':
							return this.textPredicateOpPairs;
						default:
							return null;
					}
				}),
				takeUntil(this.shutdownSubject$)
				// distinctUntilChanged()
			)
			.subscribe(this.chipOperator$);

		customFieldChanged$
			.pipe(
				map((field) => {
					return field?.view.listOfValues?.view.data;
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.customFieldDropDownDefinitions$);

		customFieldChanged$.pipe(takeUntil(this.shutdownSubject$)).subscribe(this.selectedCustomField$);

		customFieldChanged$
			.pipe(
				map((field) => field?.view.label),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.fieldLabel$);
		customFieldChanged$
			.pipe(
				map((field) => field?.view.type),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.fieldType$);

		this.initUserInputListeners();

		//set disabled state if operator changes
		this.internalModel.dataChangeRaw$.pipe(takeUntil(this.shutdownSubject$)).subscribe((dataChange) => {
			const operator = dataChange.data.operator;

			const control = this.internalCustomFieldFilter.get('value');
			if (!operator) {
				return;
			} //initialization nonsense

			if (operator.key === TextPredicateOp.AnyValue || operator.key === TextPredicateOp.NotSet) {
				if (!control.disabled) {
					control.disable({ emitEvent: dataChange.emissionType === DisambiguationEventTypes.UserChange });
				}
			} else {
				if (!control.enabled) {
					control.enable({ emitEvent: dataChange.emissionType === DisambiguationEventTypes.UserChange });
				}
			}
		});

		if (this.angularBugPatchStuff.lastWriteValueArgs) {
			this.writeValue(this.angularBugPatchStuff.lastWriteValueArgs);
			this.angularBugPatchStuff.lastWriteValueArgs = null;
			this.control.control.updateValueAndValidity({ emitEvent: false });
		}
	}

	// this relies on some specific timing, so take care when modifying
	// - child input's emit first, which allows us to safely update the rest of the form and be confident that
	// the whole form listener will emit with the unemitted changes
	private initUserInputListeners() {
		//reset subfields on typechange
		// value -> null
		// operator -> NotSet
		this.internalCustomFieldFilter.get('customField').valueChanges.subscribe((field) => {
			this.internalModel.patchValue({
				value: null,
				operator: { key: defaultOperator, label: TextPredicateOpMap.get(defaultOperator as TextPredicateOp) },
			});

			this.onTouched();
		});

		//this will emit last
		this.internalModel.userChange$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((val: ICustomFieldInternalForm) => {
				// this.internalCustomFieldFilter.markAsDirty();
				for (const control of Object.values(this.internalCustomFieldFilter.controls)) {
					control.markAsDirty();
				}
				this.outputFormData();
			});
	}

	// this allows for binding "complex data" to the options list, one value for display and one for comparisions.
	// This function does that alternate equivalency checks on just emum’s key value (the whole tuple won’t always pass an equivalency check).
	public chipComparator(optionFromChipList: ITPFilterPredicateOpPair, selection: ITPFilterPredicateOpPair) {
		if (!selection) {
			return false;
		}
		return optionFromChipList.key === selection.key;
	}

	private onValueChange = (thing: any) => {};
	public onTouched = () => {};

	// This will emit regardless of validity, which with then trigger a recalc of the validator
	private outputFormData() {
		const externalFormData = this.convertInternalToExternal(this.internalCustomFieldFilter.value);
		this.onValueChange(externalFormData);
	}

	public writeValue(value: ICustomFieldFilter) {
		if (!this.angularBugPatchStuff.initHasRan()) {
			this.angularBugPatchStuff.lastWriteValueArgs = value;
			return;
		}

		//write operator and value
		const internalVal = this.convertExternalToInternalPartial(value);
		//write customField (kinda)
		this.externalCustomFieldIdCache.next(value?.customFieldId);

		this.internalModel.patchValue(internalVal);
	}

	private convertInternalToExternal(formData: ICustomFieldInternalForm): ICustomFieldFilter | null {
		if (!formData) {
			return null;
		}
		if (!formData.customField) {
			return null;
		}

		function nullify<T>(val: T) {
			return val === undefined ? null : val;
		}

		return {
			customFieldId: nullify(formData.customField?.$id),
			operator: formData.operator.key,
			value: nullify(formData.value),
		};
	}

	//no attempt is made to convert the customFieldId -> customField sync as it depends on two streams
	private convertExternalToInternalPartial(
		formData?: ICustomFieldFilter
	): Omit<ICustomFieldInternalForm, 'customField'> {
		//undefined means "reset to undefined"
		if (!formData) {
			return { operator: undefined, value: undefined };
		}

		//set a default operator if one is not provided
		const externalOperator = formData.operator || defaultOperator;

		//tries to get a value from all three maps
		//this _will_ break if we change the NotSet string values to be different by type
		//but otherwise doesn't matter. Change at that time
		//NOTE: any solution that relies on the custom field will require handling a recalc on either operator or customField change
		const operationStringValue =
			NumericPredicateOpMap.get(externalOperator as NumericPredicateOp) ||
			TextPredicateOpMap.get(externalOperator as TextPredicateOp) ||
			DropDownPredicateOpMap.get(externalOperator as DropDownPredicateOp);

		return {
			operator: { key: externalOperator, label: operationStringValue },
			value: formData.value,
		};
	}

	public registerOnChange(fn: (value: any) => void) {
		this.onValueChange = fn;
	}

	/**
	 * ControlValueAccessor function:
	 * This is another method that registers a callback function.  This one needs to be called when this
	 * component is "touched".  We track that via the "(blur)" event handler in the HTML template, which
	 * calls the "onBlur()" method.  That in turn calls the callback method provided as a parameter here.
	 */
	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean) {
		// ngOnInitHasn't been run yet (see commentary on this.angularBugPatchStuff above)
		// if necessary this can be added to that patch, but it's probably not necessary for this to run
		// the internal default should be fine.
		if (!this.angularBugPatchStuff.initHasRan()) {
			return;
		}
		isDisabled
			? this.internalCustomFieldFilter.disable({ emitEvent: false })
			: this.internalCustomFieldFilter.enable({ emitEvent: false });
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
		this.internalModel.destroy();
	}
}
